import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { createRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useValidatedState } from 'beautiful-react-hooks'
import { intersection } from 'lodash'

import { useFontWeight } from 'common/Prose/Toolbar/hooks/useFontWeight'
import { Tooltip } from 'common/Tooltip/Tooltip'
import { FontFamilyType } from 'utils/fontUtils'

import { FontWeightDisplay } from './FontWeightDisplay'
import {
  AvailableWeights,
  fontWeightValidator,
  WeightName,
  WeightNames,
  WeightNamesMap,
  WeightValue,
  WeightValues,
} from './util'

import Styles from './FontWeight.module.scss'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import { SimpleDropdown } from 'common/Prose/Toolbar/ToolbarBravo/SimpleDropdown/SimpleDropdown'
import { SimpleDropdownItem } from 'common/Prose/Toolbar/ToolbarBravo/SimpleDropdown/SimpleDropdownItem'
import { FontWeightItem } from './FontWeightItem'
import { IconName, ToolbarIcon } from 'common/Prose/Toolbar/ToolbarIcon'

interface Props {
  className?: string
  inToolbar?: boolean
}

export const FontWeight = observer(({ className, inToolbar = false }: Props) => {
  const { setFontWeight, activeFamilies, activeWeights } = useFontWeight()
  const selfRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [optionRefs] = useState(new Map(WeightNames.map((f) => [f, createRef<HTMLDivElement>()])))
  const [isOpen, setIsOpen] = useState(false)
  const almanacEditorStateContext = useContext(AlmanacEditorStateContext)
  const fontWeightContext = almanacEditorStateContext?.fontWeight
  const fontWeightValue = fontWeightContext ? WeightNamesMap[fontWeightContext as WeightValue] : ''

  const handleFontWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFontWeightInput(e.target.value)
  }
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.setSelectionRange(0, e.target.value.length)
    setIsOpen(true)
  }

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('focusin', handleFocusLost)
      window.addEventListener('click', handleFocusLost)
      return () => {
        window.removeEventListener('focusin', handleFocusLost)
        window.removeEventListener('click', handleFocusLost)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleKeyDown = (f?: WeightName) => {
    return (e: React.KeyboardEvent) => {
      if (['Enter', ' '].includes(e.key) && fontWeightValid.valid) {
        e.preventDefault()
        setFontWeightInput(f)
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setFontWeight(f!)
        setIsOpen(false)
      }
      if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
        const direction = e.key === 'ArrowDown' ? 1 : -1
        e.preventDefault()
        const currentFontWeightIndex = filtered_weights.findIndex((fn) => fn === f) ?? 0
        const nextFontWeightIndex =
          currentFontWeightIndex + direction >= 0
            ? (currentFontWeightIndex + direction) % filtered_weights.length
            : filtered_weights.length - 1
        const nextFontWeightName = filtered_weights[nextFontWeightIndex]
        const nextFontChoiceRef = optionRefs.get(nextFontWeightName)

        nextFontChoiceRef?.current?.focus()
      }
    }
  }

  const handleChevronClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsOpen(!isOpen)
    if (!isOpen && fontWeightValid) {
      setTimeout(() => {
        inputRef.current?.focus()
        inputRef.current?.setSelectionRange(0, inputRef.current.value.length)
      }, 0)
    }
    e.preventDefault()
    e.stopPropagation()
  }

  const [fontWeightInput, setFontWeightInput, fontWeightValid] = useValidatedState(
    fontWeightValidator,
    WeightNamesMap[activeWeights[0] as WeightValue],
  )

  const handleFocusLost = useCallback(
    (e: FocusEvent | MouseEvent) => {
      if (!selfRef.current?.contains(e.target as Node) && (e.target as HTMLElement).isConnected) {
        setIsOpen(false)

        setFontWeightInput(WeightNamesMap[activeWeights[0] as WeightValue] ?? '')
      }
    },
    [activeWeights, setFontWeightInput],
  )

  useEffect(() => {
    if (activeWeights.length === 1) {
      setFontWeightInput(WeightNamesMap[activeWeights[0] as WeightValue])
    } else {
      setFontWeightInput('')
    }
  }, [activeWeights, setFontWeightInput])

  const filtered_weights = useMemo<Readonly<WeightName[]>>(() => {
    let fontWeightsList: readonly WeightValue[] = intersection(
      ...activeFamilies.map((familyName: FontFamilyType) => AvailableWeights[familyName]),
    )
    if (fontWeightsList.length === 0) {
      fontWeightsList = WeightValues
    }

    const baseNames = fontWeightsList.map((v) => WeightNamesMap[v])
    if (!fontWeightInput || baseNames.includes(fontWeightInput)) {
      return baseNames
    }
    return baseNames.flatMap((weightName) => {
      if (weightName.toLowerCase().includes(fontWeightInput.toLowerCase())) {
        return [weightName]
      }
      return []
    })
  }, [activeFamilies, fontWeightInput])

  if (inToolbar)
    return (
      <SimpleDropdown bodyClassName={Styles.list} aria-label="Change font weight" placement="right-start" isSubmenu>
        <SimpleDropdownItem
          icon={<ToolbarIcon name={IconName.FontWeight} />}
          label="Weight"
          chevronLabel={fontWeightValue}
        />
        <div>
          {filtered_weights.map((weight) => (
            <FontWeightItem
              key={weight}
              weight={weight}
              onClick={() => setFontWeight(weight)}
              ref={optionRefs.get(weight)}
              active={weight === fontWeightInput}
            />
          ))}
        </div>
      </SimpleDropdown>
    )

  return (
    <div
      className={cx(className, Styles.FontWeightDatalist)}
      ref={selfRef}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && isOpen) {
          setIsOpen(false)
        }
      }}
    >
      <Tooltip label="Select font weight" placement="top">
        <FontWeightDisplay
          data-testid="fontWeight-input"
          handleChevronClick={handleChevronClick}
          inputRef={inputRef}
          handleFontWeightChange={handleFontWeightChange}
          handleInputFocus={handleInputFocus}
          handleKeyDown={handleKeyDown(fontWeightInput)}
          fontWeightInput={fontWeightInput}
          isOpen={isOpen}
        />
      </Tooltip>

      {isOpen && (
        <div className={Styles.list}>
          {filtered_weights.map((weight) => (
            <FontWeightItem
              key={weight}
              weight={weight}
              onClick={() => {
                setFontWeight(weight)

                setFontWeightInput(weight)
                setIsOpen(false)
              }}
              onKeyDown={handleKeyDown(weight)}
              ref={optionRefs.get(weight)}
              active={weight === fontWeightInput}
            />
          ))}
        </div>
      )}
    </div>
  )
})
